import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EmbeddedViewRef,
  ViewEncapsulation,
  inject,
  input,
  InputSignal,
  contentChildren,
  Signal,
} from '@angular/core';
import {
  ButtonFill,
  ButtonSize,
  ComponentStatus,
} from '@lyceo/shared/util-models';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'button[lyceoButton], a[lyceoButton]',
  imports: [],
  template: `<ng-content />`,
  styleUrl: './button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    'class': 'lyceo-button',
    '[attr.disabled]': 'isDisabled',
    '[attr.tabindex]': 'tabbable',
    '[class.block]': 'isBlock',
    '[class.status-primary]': 'primary',
    '[class.status-secondary]': 'secondary',
    '[class.status-success]': 'success',
    '[class.status-warning]': 'warning',
    '[class.status-danger]': 'danger',
    '[class.fill-clear]': 'clear',
    '[class.fill-outline]': 'outline',
    '[class.fill-solid]': 'solid',
    '[class.size-small]': 'small',
    '[class.size-medium]': 'medium',
    '[class.icon-start]': 'iconLeft',
    '[class.icon-end]': 'iconRight',
  },
})
export class ButtonComponent implements AfterContentChecked {
  private readonly cd: ChangeDetectorRef = inject(ChangeDetectorRef);

  public readonly disabled: InputSignal<boolean> = input<boolean>(false);
  public readonly tabIndex: InputSignal<number | null> = input<number | null>(
    null,
  );
  public readonly block: InputSignal<boolean> = input<boolean>(false);
  public readonly status: InputSignal<ComponentStatus> = input<ComponentStatus>(
    ComponentStatus.PRIMARY,
  );
  public readonly fill: InputSignal<ButtonFill> = input<ButtonFill>(
    ButtonFill.SOLID,
  );
  public readonly size: InputSignal<ButtonSize> = input<ButtonSize>(
    ButtonSize.MEDIUM,
  );

  public get isDisabled(): boolean | null {
    if (this.disabled()) {
      return true;
    }

    return null;
  }

  public get tabbable(): string {
    if (this.disabled()) {
      return '-1';
    }

    const tabIndex: number | null = this.tabIndex();
    if (tabIndex === null) {
      return '0';
    }

    return tabIndex.toString();
  }

  public get isBlock(): boolean {
    return this.block();
  }

  public get primary(): boolean {
    return this.status() === ComponentStatus.PRIMARY;
  }

  public get secondary(): boolean {
    return this.status() === ComponentStatus.SECONDARY;
  }

  public get success(): boolean {
    return this.status() === ComponentStatus.SUCCESS;
  }

  public get warning(): boolean {
    return this.status() === ComponentStatus.WARNING;
  }

  public get danger(): boolean {
    return this.status() === ComponentStatus.DANGER;
  }

  public get clear(): boolean {
    return this.fill() === ButtonFill.CLEAR;
  }

  public get outline(): boolean {
    return this.fill() === ButtonFill.OUTLINE;
  }

  public get solid(): boolean {
    return this.fill() === ButtonFill.SOLID;
  }

  public get small(): boolean {
    return this.size() === ButtonSize.SMALL;
  }

  public get medium(): boolean {
    return this.size() === ButtonSize.MEDIUM;
  }

  public iconLeft: boolean = false;

  public iconRight: boolean = false;

  public readonly icons: Signal<readonly ElementRef[]> = contentChildren(
    FaIconComponent,
    { read: ElementRef },
  );

  public ngAfterContentChecked(): void {
    const firstNode: Node = this.nodes[0];
    const lastNode: Node = this.nodes[this.nodes.length - 1];

    this.iconLeft = this.isIconExist(firstNode);
    this.iconRight = this.isIconExist(lastNode);
  }

  private get nodes(): Node[] {
    return (this.cd as EmbeddedViewRef<unknown>).rootNodes.filter(
      (child: Node) => child.nodeType !== Node.COMMENT_NODE,
    );
  }

  private isIconExist(node: Node): boolean {
    return this.icons().some((item) => item.nativeElement === node);
  }
}
